import {Observable} from 'rxjs';
import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Message} from "../models/message";
import {Player} from "../models/player";
import {map} from "rxjs/operators";
import {PlayerService} from "./player.service";

interface ChatMessagePageDto {
  items: ChatMessageDto[];
  nextBeforeCreated: number;
  hasMore: boolean;
}

interface ChatMessageDto {
  id: string;
  clientMessageId?: string;
  chatId: string;
  text: string;
  createdAt: number;
  createdDateString: string;
  sender: ChatParticipantDto;
  inappropriate: boolean;
  deleted: boolean;
  likesCount: number;
  dislikesCount: number;
}

interface ChatParticipantDto {
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  contact: boolean;
  currentPlayer: boolean;
}

@Injectable()
export class MessageService {

  public apiUrl: string = environment.API_BASE_URL + '/message';

  constructor(private authHttp: HttpClient) {
  }

  public static getRequestOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
  }

  public fetchChatMessagePage(chatId: string, beforeCreated: number, limit: number): Observable<{items: Message[], nextBeforeCreated: number, hasMore: boolean}> {
    const params = [];
    if (beforeCreated) {
      params.push(`beforeCreated=${beforeCreated}`);
    }
    if (limit) {
      params.push(`limit=${limit}`);
    }
    const queryString = params.length > 0 ? `?${params.join("&")}` : "";
    return this.authHttp.get<ChatMessagePageDto>(
      `${environment.API_BASE_URL}/chats/${chatId}/messages${queryString}`,
      MessageService.getRequestOptions()).pipe(
      map((pageDto) => ({
        items: (pageDto.items || []).map((item) => this.toMessage(item)),
        nextBeforeCreated: pageDto.nextBeforeCreated,
        hasMore: pageDto.hasMore
      }))
    );
  }

  public sendMessage(message: Message): Observable<Message> {
    message.clientMessageId ||= message.localId || crypto.randomUUID();
    return this.authHttp.post<ChatMessageDto>(
      `${environment.API_BASE_URL}/chats/${message.chatId}/messages`,
      JSON.stringify({text: message.text, clientMessageId: message.clientMessageId}),
      MessageService.getRequestOptions()).pipe(
      map((savedMessage) => this.toMessage(savedMessage))
    );
  }

  private toMessage(dto: ChatMessageDto): Message {
    const message = new Message();
    message.id = dto.id;
    message.clientMessageId = dto.clientMessageId;
    message.chatId = dto.chatId;
    message.text = dto.text;
    message.sender = this.toPlayer(dto.sender);
    (message as any).created = dto.createdAt;
    message.createdDateString = dto.createdDateString;
    message.likesCount = dto.likesCount || 0;
    message.dislikesCount = dto.dislikesCount || 0;
    message.inappropriate = !!dto.inappropriate;
    message.deleted = !!dto.deleted;
    return message;
  }

  private toPlayer(dto: ChatParticipantDto): Player {
    const player = new Player();
    if (!dto) {
      return player;
    }
    player.id = dto.playerId;
    player.name = dto.name;
    player.nickName = dto.nickName;
    player.avatar = dto.avatar;
    return PlayerService.normalizePlayerAvatar(player);
  }
}
